import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	MagnifyingGlassIcon,
	Squares2X2Icon,
	TableCellsIcon,
	BuildingOfficeIcon,
	UserIcon,
} from '@heroicons/react/24/outline';

import { getContacts } from '../../api/contacts';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import Button from '../../components/Button';
import { DataTable, StatusBadge } from '../../components/nextgen';
import { AvatarGenerated } from '../../components/Avatar';
import {
	getUserFullName,
	classNames,
} from '../../components/helpers/functions';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

const VENDOR_TYPES = {
	manufacturer: 'Manufacturer',
	service_vendor: 'Service Vendor',
	distributor: 'Distributor',
	other: 'Other',
};

const ContactsList = () => {
	const navigate = useNavigate();
	const [searchQuery, setSearchQuery] = useState('');
	const [typeFilter, setTypeFilter] = useState('all');
	const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'

	const {
		value: contacts,
		status: isLoadingContacts,
		execute: refreshContacts,
	} = useAsync(getContacts, {
		defaultPayload: { pageSize: null },
	});

	const { setOpen: setContactModalOpen } = useModal('contactModal');

	// Filter and search contacts
	const filteredContacts = useMemo(() => {
		let filtered = contacts?.rows || [];

		// Apply type filter
		if (typeFilter !== 'all') {
			filtered = filtered.filter((contact) => {
				if (typeFilter === 'company') {
					return contact.type === 'company';
				} else if (typeFilter === 'person') {
					return contact.type === 'person';
				}
				return true;
			});
		}

		// Apply search filter
		if (searchQuery) {
			const search = searchQuery.toLowerCase();
			filtered = filtered.filter((contact) => {
				const name =
					contact.type === 'company'
						? contact.companyName
						: getUserFullName(contact);
				const location = contact.location || '';
				const email = contact.email || '';

				return (
					name?.toLowerCase().includes(search) ||
					location?.toLowerCase().includes(search) ||
					email?.toLowerCase().includes(search)
				);
			});
		}

		// Sort alphabetically
		return filtered.sort((a, b) => {
			const nameA = a.type === 'company' ? a.companyName : getUserFullName(a);
			const nameB = b.type === 'company' ? b.companyName : getUserFullName(b);
			return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
		});
	}, [contacts, typeFilter, searchQuery]);

	// Get contact display name
	const getContactName = (contact) => {
		return contact.type === 'company'
			? contact.companyName || 'Unnamed Company'
			: getUserFullName(contact) || 'Unnamed Contact';
	};

	// Get contact subtitle
	const getContactSubtitle = (contact) => {
		if (contact.type === 'person') {
			const parts = [];
			if (contact.title) parts.push(contact.title);
			if (contact.company?.companyName)
				parts.push(`at ${contact.company.companyName}`);
			return parts.join(' ');
		}
		return contact.location || null;
	};

	// Get vendor type badge label
	const getVendorTypeBadge = (contact) => {
		if (contact.type === 'company') {
			if (contact.location) {
				return (
					<div className="flex items-center">
						<svg
							className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
							/>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
							/>
						</svg>
						<span className="ml-1">{contact.location}</span>
					</div>
				);
			} else {
				return '';
			}
		} else {
			return contact?.title || '';
		}

		if (contact.vendorType && VENDOR_TYPES[contact.vendorType]) {
			return VENDOR_TYPES[contact.vendorType];
		}
		return contact.type === 'company' ? 'Company' : 'Individual';
	};

	// Table columns configuration
	const tableColumns = [
		{
			key: 'name',
			label: 'Name',
			sortable: true,
			render: (contact) => (
				<div className="flex items-center gap-3">
					<div className="flex-shrink-0">
						{contact.type === 'person' ? (
							<AvatarGenerated size="sm" id={getUserFullName(contact)} />
						) : (
							<AvatarGenerated size="sm" id={contact.id} type="company" />
						)}
					</div>
					<div className="min-w-0">
						<p className="text-sm font-medium text-gray-900 truncate">
							{getContactName(contact)}
						</p>
						{getContactSubtitle(contact) && (
							<p className="text-xs text-gray-500 truncate">
								{getContactSubtitle(contact)}
							</p>
						)}
					</div>
				</div>
			),
		},
		{
			key: 'type',
			label: 'Type',
			sortable: true,
			render: (contact) => (
				<div className="flex items-center gap-2">
					{contact.type === 'person' ? (
						<UserIcon className="w-4 h-4 text-gray-400" />
					) : (
						<BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
					)}
					<span className="text-sm text-gray-700">
						{getVendorTypeBadge(contact)}
					</span>
				</div>
			),
		},
		{
			key: 'contact',
			label: 'Contact',
			render: (contact) => (
				<div className="text-sm text-gray-700">
					{contact.type === 'person' ? (
						<>
							{contact.firstName} {contact.lastName}
						</>
					) : contact.contacts?.length > 0 ? (
						getUserFullName(contact.contacts[0])
					) : (
						<span className="text-gray-400">No contact</span>
					)}
				</div>
			),
		},
		{
			key: 'location',
			label: 'Location',
			sortable: true,
			render: (contact) => (
				<span className="text-sm text-gray-700">{contact.location || '—'}</span>
			),
		},
		{
			key: 'assets',
			label: 'Assets',
			sortable: true,
			className: 'text-center',
			render: (contact) => (
				<span className="text-sm text-gray-700">
					{contact._count?.assets || 0}
				</span>
			),
		},
		{
			key: 'contracts',
			label: 'Contracts',
			sortable: true,
			className: 'text-center',
			render: (contact) => (
				<span className="text-sm text-gray-700">
					{contact._count?.contracts || 0}
				</span>
			),
		},
		{
			key: 'status',
			label: 'Status',
			render: (contact) => (
				<StatusBadge
					size="sm"
					status={contact.active ? 'active' : 'inactive'}
				/>
			),
		},
	];

	return (
		<MainContent maxWidth="w-full">
			{/* Header */}
			<div className="pb-6">
				<PageHeading
					actions={[
						{
							name: 'Add Vendor',
							type: 'primary',
							onClick: () =>
								setContactModalOpen({
									onSuccess: () => refreshContacts({ pageSize: null }),
								}),
						},
					]}
					divider={false}
				>
					<div className="flex items-center justify-between">
						<div>
							<h1 className="text-2xl font-semibold text-gray-900">
								Directory
							</h1>
							<p className="mt-1 text-sm text-gray-400 text-light">
								{filteredContacts.length} vendor
								{filteredContacts.length !== 1 ? 's' : ''}
							</p>
						</div>
					</div>
				</PageHeading>
			</div>

			{/* Search and Filters */}
			<div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
				<div className="flex flex-1 items-center gap-3">
					{/* Search */}
					<div className="relative flex-1 max-w-md">
						<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
							<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
						</div>
						<input
							type="text"
							placeholder="Search vendors..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="block w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
						/>
					</div>

					{/* Type Filter Tabs */}
					<div className="flex items-center gap-2">
						<button
							onClick={() => setTypeFilter('all')}
							className={classNames(
								'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
								typeFilter === 'all'
									? 'bg-indigo-100 text-indigo-700'
									: 'text-gray-700 hover:bg-gray-100',
							)}
						>
							All
						</button>
						<button
							onClick={() => setTypeFilter('company')}
							className={classNames(
								'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
								typeFilter === 'company'
									? 'bg-indigo-100 text-indigo-700'
									: 'text-gray-700 hover:bg-gray-100',
							)}
						>
							Companies
						</button>
						<button
							onClick={() => setTypeFilter('person')}
							className={classNames(
								'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
								typeFilter === 'person'
									? 'bg-indigo-100 text-indigo-700'
									: 'text-gray-700 hover:bg-gray-100',
							)}
						>
							Individuals
						</button>
					</div>
				</div>

				{/* View Toggle
				<div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
					<button
						onClick={() => setViewMode('grid')}
						className={classNames(
							'p-2 rounded-md transition-colors',
							viewMode === 'grid'
								? 'bg-white text-gray-900 shadow-sm'
								: 'text-gray-500 hover:text-gray-700',
						)}
						title="Grid view"
					>
						<Squares2X2Icon className="h-5 w-5" />
					</button>
					<button
						onClick={() => setViewMode('table')}
						className={classNames(
							'p-2 rounded-md transition-colors',
							viewMode === 'table'
								? 'bg-white text-gray-900 shadow-sm'
								: 'text-gray-500 hover:text-gray-700',
						)}
						title="Table view"
					>
						<TableCellsIcon className="h-5 w-5" />
					</button>
				</div> */}
			</div>

			{/* Content */}
			{isLoadingContacts === 'pending' ? (
				<div className="text-center py-12">
					<p className="text-sm text-gray-500">Loading vendors...</p>
				</div>
			) : filteredContacts.length === 0 ? (
				<div className="text-center py-12 bg-white rounded-lg border border-gray-200">
					<BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
					<h3 className="mt-2 text-sm font-medium text-gray-900">
						No vendors found
					</h3>
					<p className="mt-1 text-sm text-gray-500">
						{searchQuery || typeFilter !== 'all'
							? 'Try adjusting your search or filters.'
							: 'Get started by creating a new vendor.'}
					</p>
					{!searchQuery && typeFilter === 'all' && (
						<div className="mt-6">
							<Button
								type="primary"
								onClick={() =>
									setContactModalOpen({
										onSuccess: () => refreshContacts({ pageSize: null }),
									})
								}
							>
								Add Vendor
							</Button>
						</div>
					)}
				</div>
			) : viewMode === 'grid' ? (
				/* Grid View */
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{filteredContacts.map((contact) => (
						<div
							key={contact.id}
							onClick={() => navigate(`/manage/directory/${contact.id}`)}
							className="relative bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer"
						>
							{/* Avatar and Name */}
							<div className="flex gap-3 mb-3">
								<div className="flex-shrink-0">
									{contact.type === 'person' ? (
										<AvatarGenerated size="xs" id={getUserFullName(contact)} />
									) : (
										<AvatarGenerated size="xs" id={contact.companyName} />
									)}
								</div>
								<div className="min-w-0 flex-1 flex items-center">
									<h3 className="text-sm font-semibold text-gray-900 truncate">
										{getContactName(contact)}
									</h3>
								</div>
							</div>

							{/* Stats */}
							<div className="flex items-center gap-4 pt-3 border-t border-gray-100">
								<div className="flex items-center gap-1.5">
									<svg
										className="w-4 h-4 text-gray-400"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
										/>
									</svg>
									<span className="text-sm font-medium text-gray-700">
										{contact._count?.assets || 0}
									</span>
									<span className="text-xs text-gray-500">Assets</span>
								</div>
								<div className="flex items-center gap-1.5">
									<svg
										className="w-4 h-4 text-gray-400"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
									<span className="text-sm font-medium text-gray-700">
										{contact._count?.contracts || 0}
									</span>
									<span className="text-xs text-gray-500">Contracts</span>
								</div>
							</div>
						</div>
					))}
				</div>
			) : (
				/* Table View */
				<div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
					<DataTable
						data={filteredContacts}
						columns={tableColumns}
						onRowClick={(contact) =>
							navigate(`/manage/directory/${contact.id}`)
						}
						emptyMessage="No vendors found"
						sortable
					/>
				</div>
			)}
		</MainContent>
	);
};

export default ContactsList;
